import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// Firebase configuration
import { db, auth } from "./firebase";

// Components
import AppHeader from "./AppHeader"; // AppHeader-ai ingae import seithuvullen

// ---------- Styled Components ----------

const PageWrapper = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
`;

const MainContent = styled.main`
  padding: 40px 20px;
  max-width: 1100px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 2px solid #eaeaea;
  padding-bottom: 15px;
`;

const Title = styled.h2`
  font-size: 24px;
  color: #202124;
  font-weight: 700;
  margin: 0;
`;

const ConnectionCount = styled.span`
  background: #1a73e8;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 25px;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  border: 1px solid #ebebeb;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 16px;
`;

const Avatar = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Name = styled.h4`
  margin: 8px 0 4px 0;
  font-size: 17px;
  color: #1a202c;
  font-weight: 600;
`;

const Username = styled.p`
  margin-bottom: 20px;
  font-size: 13px;
  color: #70757a;
`;

const ViewButton = styled.button`
  background-color: #fff;
  color: #1a73e8;
  border: 1.5px solid #1a73e8;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s;

  &:hover {
    background-color: #1a73e8;
    color: white;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  color: #5f6368;
`;

// ---------- Component Logic ----------

const MyConnections = () => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchConnections = useCallback(async (currentUserUid) => {
    try {
      setLoading(true);
      const connRef = collection(db, "connections");
      
      const q1 = query(connRef, where("receiverId", "==", currentUserUid), where("status", "==", "connected"));
      const q2 = query(connRef, where("senderId", "==", currentUserUid), where("status", "==", "connected"));

      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      
      const otherUserUids = [];
      snap1.forEach(doc => otherUserUids.push(doc.data().senderId));
      snap2.forEach(doc => otherUserUids.push(doc.data().receiverId));

      if (otherUserUids.length === 0) {
        setConnections([]);
        return;
      }

      const profilesRef = collection(db, "profiles");
      const profileData = await Promise.all(
        otherUserUids.map(async (uid) => {
          const pq = query(profilesRef, where("uid", "==", uid));
          const psnap = await getDocs(pq);
          if (!psnap.empty) {
            const data = psnap.docs[0].data();
            return { 
              id: psnap.docs[0].id, 
              ...data,
              displayName: data.fullName || "User",
              displayUser: data.userName && data.userName !== "unknown" 
                ? data.userName 
                : (data.email ? data.email.split('@')[0] : "user")
            };
          }
          return null;
        })
      );

      setConnections(profileData.filter(p => p !== null));
    } catch (error) {
      console.error("Error fetching connections:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchConnections(user.uid);
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [fetchConnections, navigate]);

  return (
    <PageWrapper>
      <AppHeader />
      
      <MainContent>
        <HeaderSection>
          <Title>My Network</Title>
          {!loading && (
            <ConnectionCount>{connections.length} Connections</ConnectionCount>
          )}
        </HeaderSection>
        
        {loading ? (
          <div style={{ textAlign: "center", padding: "100px" }}>
            <p>Finding your connections...</p>
          </div>
        ) : connections.length === 0 ? (
          <EmptyState>
            <h3>No connections yet</h3>
            <p>Grow your network by discovering and connecting with people.</p>
          </EmptyState>
        ) : (
          <CardsGrid>
            {connections.map((user) => (
              <ProfileCard key={user.id}>
                <AvatarWrapper>
                  <Avatar 
                    src={user.profileImageUrl || "/default-avatar.png"} 
                    alt={user.displayName} 
                    onError={(e) => { e.target.src = "/default-avatar.png"; }}
                  />
                </AvatarWrapper>
                <Name>{user.displayName}</Name>
                <Username>@{user.displayUser}</Username>
                
                {/* Kizhe ulla ViewButton-il navigate logic-ai 
                  id-ku bathilaga fullName (spaces neekki) payanpaduthum padi maatriyullen 
                */}
                <ViewButton onClick={() => navigate(`/profile/${encodeURIComponent(user.displayName.replace(/\s+/g, ''))}`)}>
                  View Profile
                </ViewButton>
              </ProfileCard>
            ))}
          </CardsGrid>
        )}
      </MainContent>
    </PageWrapper>
  );
};

export default MyConnections;